import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { RootState, store } from "@store/store";
import { AppConfig } from "@models/appConfig";
import {
  CONVERSATION_PACING_PRESETS,
  CONVERSATION_PACING_VALUES,
  ConversationPacing,
  VOICE_INPUT_MODE_VALUES,
  VoiceInputMode,
} from "@models/conversation-settings";
import { debugCompanionPacing } from "@utils/companion-pacing-debug";

const STORAGE_KEY = "argoli.companionConversation.v1";

interface StoredConversationSettings {
  conversationPacing?: ConversationPacing;
  voiceInputMode?: VoiceInputMode;
  vadBargeInEnabled?: boolean;
}

function readStoredSettings(): StoredConversationSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoredConversationSettings;
    return parsed ?? {};
  } catch {
    return {};
  }
}

function saveStoredSettings(next: StoredConversationSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage failures; runtime settings still work.
  }
}

function isConversationPacing(value: string): value is ConversationPacing {
  return (CONVERSATION_PACING_VALUES as readonly string[]).includes(value);
}

function isVoiceInputMode(value: string): value is VoiceInputMode {
  return (VOICE_INPUT_MODE_VALUES as readonly string[]).includes(value);
}

export interface MicBridge {
  start: () => void;
  stop: () => void;
  /**
   * Stop PTT capture without firing end-talk semantics (no LLM turn).
   * Used when the in-flight utterance is superseded (e.g. gallery slide).
   */
  abort: () => void;
}

interface CompanionConversationContextValue {
  conversationPacing: ConversationPacing;
  setConversationPacing: (value: ConversationPacing) => void;
  voiceInputMode: VoiceInputMode;
  setVoiceInputMode: (value: VoiceInputMode) => void;
  vadBargeInEnabled: boolean;
  setVadBargeInEnabled: (value: boolean) => void;
  pauseDurationMs: number;
  ttsEvictionMs: number;
  canStartUserCapture: (processing: boolean) => boolean;
  /** Same rules as `canStartUserCapture` but reads latest Redux avatar slice (avoids stale refs right after interrupt). */
  canStartUserCaptureLive: () => boolean;
  startPttCapture: () => void;
  stopPttCapture: () => void;
  /** Abort PTT capture (no end-talk signal) - used on gallery slide. */
  abortPttCapture: () => void;
  registerMicBridge: (bridge: MicBridge) => void;
  unregisterMicBridge: () => void;
  registerPttInterruptHandler: (
    handler: (() => void | Promise<void>) | null,
  ) => void;
  runPttInterrupt: () => Promise<void>;
  /**
   * Toolbar-side hook to drop a primed PTT auto-resume (e.g. when a gallery slide
   * interrupts the current assistant playback). Without this, the slide-induced
   * `talking=false` transition starts the mic during the `NewPhoto` debounce window
   * and the subsequent reply's subtitles get clobbered by the background PCM flow.
   */
  registerPttAutoResumeCanceler: (canceler: (() => void) | null) => void;
  cancelPttAutoResume: () => void;
}

const CompanionConversationContext =
  createContext<CompanionConversationContextValue | null>(null);

export const CompanionConversationProvider: FC<
  PropsWithChildren<{ appConfig: AppConfig }>
> = ({ appConfig, children }) => {
  const settingsRef = useRef<StoredConversationSettings>(readStoredSettings());
  const [conversationPacing, _setConversationPacing] = React.useState<
    ConversationPacing
  >(() => {
    const stored = settingsRef.current.conversationPacing;
    return stored && isConversationPacing(stored)
      ? stored
      : appConfig.defaultConversationPacing;
  });
  const [voiceInputMode, _setVoiceInputMode] = React.useState<VoiceInputMode>(
    () => {
      const stored = settingsRef.current.voiceInputMode;
      return stored && isVoiceInputMode(stored)
        ? stored
        : appConfig.defaultVoiceInputMode;
    },
  );
  const [vadBargeInEnabled, _setVadBargeInEnabled] = React.useState<boolean>(
    () => Boolean(settingsRef.current.vadBargeInEnabled),
  );
  const talking = useSelector((s: RootState) => s.avatar.talking);
  const pttInterruptHandlerRef = useRef<(() => void | Promise<void>) | null>(
    null,
  );
  const pttAutoResumeCancelerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const preset = CONVERSATION_PACING_PRESETS[conversationPacing];
    debugCompanionPacing("conversation settings (effective preset)", {
      conversationPacing,
      voiceInputMode,
      pauseDurationMs: preset.pauseDurationMs,
      ttsEvictionMs: preset.ttsEvictionMs,
      twoPhaseInitialMs: preset.twoPhaseInitialMs,
      twoPhaseAdditionalMs: preset.twoPhaseAdditionalMs,
    });
  }, [conversationPacing, voiceInputMode]);

  const micBridgeRef = useRef<MicBridge | null>(null);

  const setConversationPacing = useCallback((value: ConversationPacing) => {
    settingsRef.current = { ...settingsRef.current, conversationPacing: value };
    saveStoredSettings(settingsRef.current);
    _setConversationPacing(value);
  }, []);

  const setVoiceInputMode = useCallback((value: VoiceInputMode) => {
    settingsRef.current = { ...settingsRef.current, voiceInputMode: value };
    saveStoredSettings(settingsRef.current);
    _setVoiceInputMode(value);
  }, []);

  const setVadBargeInEnabled = useCallback((value: boolean) => {
    settingsRef.current = { ...settingsRef.current, vadBargeInEnabled: value };
    saveStoredSettings(settingsRef.current);
    _setVadBargeInEnabled(value);
  }, []);

  const registerMicBridge = useCallback((bridge: MicBridge) => {
    micBridgeRef.current = bridge;
  }, []);

  const unregisterMicBridge = useCallback(() => {
    micBridgeRef.current = null;
  }, []);

  const startPttCapture = useCallback(() => {
    micBridgeRef.current?.start();
  }, []);

  const stopPttCapture = useCallback(() => {
    micBridgeRef.current?.stop();
  }, []);

  const abortPttCapture = useCallback(() => {
    micBridgeRef.current?.abort();
  }, []);

  const registerPttInterruptHandler = useCallback(
    (handler: (() => void | Promise<void>) | null) => {
      pttInterruptHandlerRef.current = handler;
    },
    [],
  );

  const runPttInterrupt = useCallback(async () => {
    const fn = pttInterruptHandlerRef.current;
    if (fn) {
      await fn();
    }
  }, []);

  const registerPttAutoResumeCanceler = useCallback(
    (canceler: (() => void) | null) => {
      pttAutoResumeCancelerRef.current = canceler;
    },
    [],
  );

  const cancelPttAutoResume = useCallback(() => {
    pttAutoResumeCancelerRef.current?.();
  }, []);

  const canStartUserCapture = useCallback(
    (processingArg: boolean) => {
      if (voiceInputMode === "vad") {
        if (vadBargeInEnabled) {
          return !processingArg || talking;
        }
        return !processingArg;
      }
      return !processingArg;
    },
    [talking, vadBargeInEnabled, voiceInputMode],
  );

  const canStartUserCaptureLive = useCallback((): boolean => {
    const { processing: processingLive, talking: talkingLive } =
      store.getState().avatar;
    if (voiceInputMode === "vad") {
      if (vadBargeInEnabled) {
        return !processingLive || talkingLive;
      }
      return !processingLive;
    }
    return !processingLive;
  }, [vadBargeInEnabled, voiceInputMode]);

  const value = useMemo<CompanionConversationContextValue>(() => {
    const preset = CONVERSATION_PACING_PRESETS[conversationPacing];
    return {
      conversationPacing,
      setConversationPacing,
      voiceInputMode,
      setVoiceInputMode,
      vadBargeInEnabled,
      setVadBargeInEnabled,
      pauseDurationMs: preset.pauseDurationMs,
      ttsEvictionMs: preset.ttsEvictionMs,
      canStartUserCapture,
      canStartUserCaptureLive,
      startPttCapture,
      stopPttCapture,
      abortPttCapture,
      registerMicBridge,
      unregisterMicBridge,
      registerPttInterruptHandler,
      runPttInterrupt,
      registerPttAutoResumeCanceler,
      cancelPttAutoResume,
    };
  }, [
    abortPttCapture,
    cancelPttAutoResume,
    canStartUserCapture,
    canStartUserCaptureLive,
    conversationPacing,
    registerMicBridge,
    registerPttAutoResumeCanceler,
    registerPttInterruptHandler,
    runPttInterrupt,
    setConversationPacing,
    setVoiceInputMode,
    setVadBargeInEnabled,
    startPttCapture,
    stopPttCapture,
    unregisterMicBridge,
    vadBargeInEnabled,
    voiceInputMode,
  ]);

  return (
    <CompanionConversationContext.Provider value={value}>
      {children}
    </CompanionConversationContext.Provider>
  );
};

export function useCompanionConversation(): CompanionConversationContextValue {
  const ctx = useContext(CompanionConversationContext);
  if (!ctx) {
    throw new Error("useCompanionConversation must be used within provider");
  }
  return ctx;
}
